/**
 * F-136 — the design theme as TEXT, for the clipboard.
 *
 * The art direction is the one thing on a board that is written to be read
 * somewhere else: pasted into a brief for a freelance animator, into the
 * prompt of whatever image tool someone is trying this week, into the message
 * that asks a designer for one more frame. Before this, "copy the design" meant
 * selecting eight textareas one at a time — and a textarea's selection stops at
 * its own box, so nobody ever got the palette.
 *
 * The rendered order is the PANEL's order (palette, then `FIELD_ORDER`), not
 * storage order: what you copy should be what you just read.
 */
import type { ArtDirection, PaletteSwatch } from "@kola/shared";

import { copyToClipboard } from "../../core/share-link.js";
import { showToast } from "../../ui/toast.js";
import { FIELD_COPY, FIELD_ORDER } from "./fields.js";

/** `#0b1d26 — background`, or the bare hex while the role is still unnamed. */
function swatchLine(swatch: PaletteSwatch): string {
  const name = swatch.name.trim();
  return name === "" ? swatch.hex : `${swatch.hex} — ${name}`;
}

/**
 * The art direction as plain text — headings and blank lines, no Markdown.
 * It is pasted into chat windows, mail and prompt boxes as often as into a
 * document, and `**Mood**` reads as noise in three of those four.
 *
 * EMPTY FIELDS ARE OMITTED, not printed as empty headings: a half-written
 * theme should paste as the half that exists. An art direction with nothing in
 * it at all answers "" — the callers show a toast rather than putting an empty
 * clipboard over whatever the user had copied before.
 */
export function artDirectionText(art: ArtDirection): string {
  const blocks: string[] = [];

  if (art.palette.length > 0) {
    blocks.push(["Palette", ...art.palette.map(swatchLine)].join("\n"));
  }

  for (const field of FIELD_ORDER) {
    const value = art[field].trim();
    if (value !== "") blocks.push(`${FIELD_COPY[field].label}\n${value}`);
  }

  return blocks.join("\n\n");
}

/**
 * Copy the description, and say what happened. `copyToClipboard` answers
 * false rather than throwing when the clipboard is unreachable (an insecure
 * origin, a denied permission), and silence there is the worst outcome: the
 * user walks off believing they have the text.
 */
export async function copyDesignDescription(art: ArtDirection): Promise<void> {
  const text = artDirectionText(art);
  if (text === "") {
    showToast("There's no art direction to copy yet");
    return;
  }
  const copied = await copyToClipboard(text);
  showToast(copied ? "Design description copied" : "Couldn't reach the clipboard");
}
